import React from 'react';
import { Users, TrendingUp, Target, Share2 } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useGA4 } from '../../contexts/GA4Context';
import TimeRangeSelector from '../../components/analytics/TimeRangeSelector';
import MetricCard from '../../components/analytics/MetricCard';
import DataTable from '../../components/analytics/DataTable';
import { formatNumber, formatPercentage, formatDuration } from '../../utils/analytics-helpers';
import './AcquisitionAnalytics.css';

const AcquisitionAnalytics = () => {
  const { loading, error, metrics, sources } = useGA4();

  // Channel colors
  const CHANNEL_COLORS = {
    'Organic Search': '#3B82F6',
    'Direct': '#8B5CF6',
    'Referral': '#EC4899',
    'Social': '#F59E0B',
    'Paid Search': '#10B981',
    'Email': '#6366F1',
    'Other': '#6B7280'
  };

  // Prepare channel data for charts
  const channelData = sources?.slice(0, 6).map(s => ({
    channel: s.source || 'Direct',
    sessions: s.sessions || 0,
    users: s.users || 0
  })) || [];

  // Table columns
  const sourcesColumns = [
    { key: 'source', label: 'Source', sortable: true },
    { key: 'medium', label: 'Medium', sortable: true },
    { key: 'sessions', label: 'Sessions', sortable: true, format: (val) => formatNumber(val) },
    { key: 'users', label: 'Users', sortable: true, format: (val) => formatNumber(val) },
    { key: 'newUsers', label: 'New Users', sortable: true, format: (val) => formatNumber(val) },
    { key: 'engagementRate', label: 'Engagement', sortable: true, format: (val) => formatPercentage(val) }
  ];

  return (
    <div className="acquisition-analytics">
      <div className="acquisition-header">
        <div>
          <h2>Acquisition Analytics</h2>
          <p className="acquisition-subtitle">Traffic sources, campaigns, and user acquisition</p>
        </div>
        <TimeRangeSelector />
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading acquisition data...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            <MetricCard
              title="Total Sessions"
              value={metrics?.sessions || 0}
              previousValue={metrics?.previousSessions}
              format="number"
              icon={Users}
              subtitle="All visits"
            />
            <MetricCard
              title="New Users"
              value={metrics?.newUsers || 0}
              previousValue={metrics?.previousNewUsers}
              format="number"
              icon={TrendingUp}
              subtitle="First-time visitors"
            />
            <MetricCard
              title="Engagement Rate"
              value={metrics?.engagementRate || 0}
              previousValue={metrics?.previousEngagementRate}
              format="percentage"
              icon={Target}
              subtitle="Engaged sessions"
            />
            <MetricCard
              title="Active Users"
              value={metrics?.activeUsers || 0}
              previousValue={metrics?.previousActiveUsers}
              format="number"
              icon={Share2}
              subtitle="Current period"
            />
          </div>

          {/* Channel Breakdown Charts */}
          <div className="charts-row">
            <div className="chart-container">
              <h3>Sessions by Channel</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={channelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="channel"
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar dataKey="sessions" fill="#3B82F6">
                    {channelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHANNEL_COLORS[entry.channel] || '#6B7280'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h3>Users by Channel</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={channelData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ channel, percent }) =>
                      `${channel} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="users"
                  >
                    {channelData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHANNEL_COLORS[entry.channel] || '#6B7280'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detailed Sources Table */}
          <div className="table-container">
            <DataTable
              data={sources || []}
              columns={sourcesColumns}
              title="All Traffic Sources"
              searchable={true}
              exportable={true}
              pageSize={25}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AcquisitionAnalytics;
